// Utilities to process glossary placeholders and clean language prefixes

// Locale-agnostic glossary with human-readable keys
export const GLOSSARY = {
  NIE: { 
    en: "NIE (Foreigner Identification Number)",
    es: "NIE (Número de Identidad de Extranjero)",
    fr: "NIE (Numéro d'Identification d'Étranger)" 
  },
  TIE: { 
    en: "TIE (Foreigners Identity Card)",
    es: "TIE (Tarjeta de Identidad de Extranjero)",
    fr: "TIE (Carte d'Identité d'Étranger)" 
  },
  EMPADRONAMIENTO: { 
    en: "Empadronamiento (Municipal Registration)",
    es: "Empadronamiento",
    fr: "Empadronamiento (inscription municipale)" 
  },
  PADRON: { 
    en: "Padrón (Municipal Register)",
    es: "Padrón",
    fr: "Padrón (registre municipal)" 
  },
  DIGITAL_NOMAD_VISA: { 
    en: "Digital Nomad Visa",
    es: "Visa de Nómada Digital",
    fr: "Visa Nomade Numérique" 
  },
  BECKHAM_LAW: { 
    en: "Beckham Law (Special Tax Regime)",
    es: "Ley Beckham (Régimen Fiscal Especial)",
    fr: "Loi Beckham (Régime Fiscal Spécial)" 
  },
  YEAR_2025: { 
    en: "2025",
    es: "2025",
    fr: "2025" 
  }
};

// Token pattern: {{G:KEY}}
const G_TOKEN = /\{\{G:([A-Z0-9_]+)\}\}/g;

export function replaceGlossaryPlaceholders(text, locale) {
  if (typeof text !== 'string') return text;
  const loc = (locale || 'en').split('-')[0];
  
  return text.replace(G_TOKEN, (_, key) => {
    return GLOSSARY[key]?.[loc] ?? GLOSSARY[key]?.en ?? key;
  });
}

// Legacy support for old numeric tokens (temporary)
function replaceLegacyGlossaryPlaceholders(text, locale) {
  if (typeof text !== 'string') return text;
  const loc = (locale || 'en').split('-')[0];
  
  // Legacy mapping for backward compatibility
  const legacyMap = {
    en: { 
      1: '2024',
      2: '2025', 
      4: 'NIE (Foreigner Identification Number)', 
      5: 'TIE (Foreigners Identity Card)', 
      12: 'Grandchildren Law',
      11: 'Digital Nomad Visa', 
      13: 'Empadronamiento (Municipal Registration)', 
      14: 'Padrón (Municipal Register)',
      18: 'Return Permit',
      19: 'ETIAS',
      20: 'Low Emission Zone (LEZ)',
      37: 'Madrid',
      38: 'Barcelona',
      39: 'Valencia',
      41: 'Bilbao',
      42: 'Málaga',
      43: '€'
    },
    es: { 
      1: '2024',
      2: '2025', 
      4: 'NIE (Número de Identidad de Extranjero)', 
      5: 'TIE (Tarjeta de Identidad de Extranjero)', 
      12: 'Ley de Nietos',
      11: 'Visa de Nómada Digital', 
      13: 'Empadronamiento', 
      14: 'Padrón',
      18: 'Permiso de Regreso',
      19: 'ETIAS',
      20: 'Zona de Bajas Emisiones (ZBE)',
      37: 'Madrid',
      38: 'Barcelona',
      39: 'Valencia',
      41: 'Bilbao',
      42: 'Málaga',
      43: '€',
      29: 'Tarjeta Azul'
    },
    fr: { 
      1: '2024',
      2: '2025', 
      4: "NIE (Numéro d'Identification d'Étranger)", 
      5: "TIE (Carte d'Identité d'Étranger)", 
      12: 'Loi des Petits-Enfants',
      11: 'Visa Nomade Numérique', 
      13: 'Empadronamiento (inscription municipale)', 
      14: 'Padrón (registre municipal)',
      18: 'Autorisation de Retour',
      19: 'ETIAS',
      20: 'Zone à faibles émissions (ZFE)',
      37: 'Madrid',
      38: 'Barcelona',
      39: 'Valencia',
      41: 'Bilbao',
      42: 'Málaga',
      43: '€',
      29: 'Carte Bleue UE'
    }
  };
  
  const map = legacyMap[loc] || legacyMap.en;
  let out = text;
  // Replace both __GLOSSARY_n__ and __GLOSARIO_n__
  out = out.replace(/__(?:GLOSSARY|GLOSARIO)_(\d+)__/g, (m, numStr) => {
    const num = Number(numStr);
    return map[num] || m;
  });
  return out;
}

function restoreAndReplace(text, locale) {
  if (typeof text !== 'string') return text;
  // Remove leading locale prefixes like [es] or [fr]
  let out = text.replace(/^\s*\[(?:es|fr)\]\s*/i, '');
  // Replace new glossary tokens first
  out = replaceGlossaryPlaceholders(out, locale);
  // Then handle legacy tokens for backward compatibility
  out = replaceLegacyGlossaryPlaceholders(out, locale);
  // Remove stray empty parentheses left after placeholder/link cleanup
  out = out.replace(/\s*\(\s*\)/g, '');
  // Collapse multiple spaces/tabs but preserve newlines for Markdown formatting
  out = out.replace(/[^\S\r\n]{2,}/g, ' ');
  return out;
}

export function sanitizeConfig(config, locale) {
  if (!config) return config;
  const walk = (val) => {
    if (typeof val === 'string') return restoreAndReplace(val, locale);
    if (Array.isArray(val)) return val.map(walk);
    if (val && typeof val === 'object') {
      const out = {};
      for (const [k, v] of Object.entries(val)) out[k] = walk(v);
      return out;
    }
    return val;
  };
  return walk(config);
}


